// src/context/LocationContext.js

import { createContext, useContext, useState, useEffect } from "react";
import { getUserAddressByUserId } from "../services/Navbar/LocationServices";
import { UserContext } from "./UserContext";

const LocationContext = createContext();

export function LocationProvider({ children }) {
  const { user } = useContext(UserContext);

  const [liveCoords, setLiveCoords] = useState({ lat: null, lng: null });
  const [selectedCity, setSelectedCity] = useState(null);
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const GOOGLE_API_KEY = "AIzaSyAlqS1FMKTkCJoaOHzghrXA_6PA6hflHCo";

  // ✅ DEFAULT FALLBACK LOCATION (Delhi)
  const DEFAULT_LOCATION = {
    lat: 28.6139,
    lng: 77.2090,
    address: "Delhi, India"
  };

  // ================= LIVE LOCATION =================
  const getLiveCoords = () => {
    return new Promise((resolve, reject) => {
      console.log("🌍 Getting LIVE location...");

      // ✅ Check if geolocation is supported
      if (!navigator.geolocation) {
        console.log("❌ Geolocation not supported");
        reject(new Error("Geolocation not supported"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const data = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          console.log("🌍 LIVE COORDS:", data);
          resolve(data);
        },
        (err) => {
          console.log("❌ GPS ERROR:", err.message);
          reject(err);
        },
        { 
          enableHighAccuracy: true, 
          timeout: 10000,
          maximumAge: 60000 
        }
      );
    });
  };

  // ================= ADDRESS =================
  const getAddressFromCoords = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_API_KEY}`
      );
      const data = await res.json();
      return data.results?.[0]?.formatted_address || "Address not found";
    } catch {
      return "Failed to get address";
    }
  };

  // ================= FETCH LOCATION =================
  const fetchLocation = async () => {
    setLoading(true);
    setError("");

    try {
      let live = null;

      // ✅ Try to get live location
      try {
        live = await getLiveCoords();
        setLiveCoords(live);

        const liveAddress = await getAddressFromCoords(live.lat, live.lng);
        setAddress(liveAddress);
        setCoords(live); // ✅ Set coords immediately
        console.log("✅ Live location set:", live);
      } catch (err) {
        console.log("⚠️ Live location failed, trying fallback...");
      }

      // ✅ If live location failed, try DB location
      if (!live && user?.id) {
        try {
          const data = await getUserAddressByUserId(user.id);
          if (data?.length) {
            const first = data[0];
            live = {
              lat: parseFloat(first.latitude),
              lng: parseFloat(first.longitude),
            };
            setLiveCoords(live);
            setCoords(live); // ✅ Set coords from DB
            setAddress([first.faltHousNumber, first.street, first.locality, first.pincode]
              .filter(Boolean)
              .join(", "));
            console.log("✅ DB location set:", live);
          }
        } catch (dbErr) {
          console.log("⚠️ DB location failed");
        }
      }

      // ✅ FINAL FALLBACK: If no location found, use default
      if (!live) {
        console.log("⚠️ No location found, using DEFAULT location (Delhi)");
        setCoords({
          lat: DEFAULT_LOCATION.lat,
          lng: DEFAULT_LOCATION.lng
        });
        setAddress(DEFAULT_LOCATION.address);
        setLiveCoords({
          lat: DEFAULT_LOCATION.lat,
          lng: DEFAULT_LOCATION.lng
        });
      }

    } catch (err) {
      console.log("❌ Location fetch failed:", err);
      // ✅ Even on error, set default location
      setCoords({
        lat: DEFAULT_LOCATION.lat,
        lng: DEFAULT_LOCATION.lng
      });
      setAddress(DEFAULT_LOCATION.address);
      setError("Using default location");
    } finally {
      setLoading(false);
    }
  };

  // ================= CITY SELECT =================
  const setCityManually = async (city) => {
    let lat = parseFloat(city?.latitude);
    let lng = parseFloat(city?.longitude);

    console.log("🏙️ CITY SELECT:", city);

    if (!lat || !lng) return;

    const cityData = {
      name: city?.name || city?.cityName || city?.city || "Selected City",
      latitude: lat,
      longitude: lng,
    };

    setSelectedCity(cityData);
    setCoords({ lat, lng });
    setAddress(cityData.name);

    localStorage.setItem("selectedCity", JSON.stringify(cityData));
  };

  // ================= CLEAR CITY =================
  const clearManualCity = () => {
    console.log("📡 SWITCHING BACK TO LIVE LOCATION");
    setSelectedCity(null);
    localStorage.removeItem("selectedCity");

    if (liveCoords.lat && liveCoords.lng) {
      setCoords(liveCoords);
    } else {
      // ✅ If no live coords, use default
      setCoords({
        lat: DEFAULT_LOCATION.lat,
        lng: DEFAULT_LOCATION.lng
      });
      setAddress(DEFAULT_LOCATION.address);
    }
  };

  // ================= UPDATE LOCATION =================
  const updateLocation = (newAddress) => {
    setAddress(newAddress);
  };

  // ================= INITIAL LOAD =================
  useEffect(() => {
    fetchLocation();
  }, [user?.id]);

  // ================= FINAL COORDS =================
  useEffect(() => {
    if (selectedCity?.latitude && selectedCity?.longitude) {
      setCoords({
        lat: parseFloat(selectedCity.latitude),
        lng: parseFloat(selectedCity.longitude),
      });
      console.log("✅ Using SELECTED CITY coords:", selectedCity.name);
    } else if (liveCoords.lat && liveCoords.lng) {
      setCoords(liveCoords);
      console.log("✅ Using LIVE coords");
    } else {
      // ✅ Fallback: Use default if nothing else
      setCoords({
        lat: DEFAULT_LOCATION.lat,
        lng: DEFAULT_LOCATION.lng
      });
      console.log("⚠️ Using DEFAULT coords (Delhi)");
    }
  }, [selectedCity, liveCoords]);

  // ================= DEBUG =================
  useEffect(() => {
    console.log("📍 FINAL COORDS 👉", coords);
    console.log("🏙️ ADDRESS 👉", address);
  }, [coords, address]);

  return (
    <LocationContext.Provider
      value={{
        coords,
        address,
        loading,
        error,
        fetchLocation,
        setCityManually,
        clearManualCity,
        selectedCity,
        updateLocation,
        setAddress,
        liveCoords, // ✅ Export for debugging
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}

// ✅ FIXED HOOK
export function useLocationContext() {
  return useContext(LocationContext);
}